<script setup>
// Skaner kodów kreskowych: natywny BarcodeDetector, a gdy go nie ma albo nie zna
// naszych formatów — ponyfill na WASM. Kamera zatrzymuje się przy zamknięciu,
// żeby nie świeciła diodą po schowaniu okna.
import { nextTick, onBeforeUnmount, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { BarcodeDetector as WasmBarcodeDetector } from 'barcode-detector/pure';

const { t } = useI18n();

const props = defineProps({
    show: { type: Boolean, default: false },
});
const emit = defineEmits(['close', 'detected']);

// Formaty kodów sklepowych (nazewnictwo BarcodeDetector API).
const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf'];

const videoEl = ref(null);
const error = ref(null);
const manual = ref('');

let stream = null;
let rafId = null;
let stopped = true;

const stop = () => {
    stopped = true;
    if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
    if (stream) {
        stream.getTracks().forEach(t => t.stop());
        stream = null;
    }
};

const close = () => { stop(); emit('close'); };

const found = (code) => {
    if (!code) return;
    stop();
    emit('detected', String(code));
};

// Natywny detektor tylko wtedy, gdy naprawdę zna nasze formaty.
const resolveDetector = async () => {
    if ('BarcodeDetector' in window) {
        try {
            const supported = await window.BarcodeDetector.getSupportedFormats();
            if (FORMATS.some(f => supported.includes(f))) return window.BarcodeDetector;
        } catch { /* przejdź na WASM */ }
    }
    return WasmBarcodeDetector;
};

const start = async () => {
    error.value = null;
    stopped = false;
    await nextTick();
    try {
        const Detector = await resolveDetector();
        const supported = await Detector.getSupportedFormats();
        const formats = FORMATS.filter(f => supported.includes(f));
        const detector = new Detector(formats.length ? { formats } : undefined);

        stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        });
        videoEl.value.srcObject = stream;
        await videoEl.value.play();

        const scan = async () => {
            if (stopped) return;
            try {
                const codes = await detector.detect(videoEl.value);
                if (codes?.length) { found(codes[0].rawValue); return; }
            } catch { /* pojedyncze klatki mogą się nie udać */ }
            if (!stopped) rafId = requestAnimationFrame(scan);
        };
        rafId = requestAnimationFrame(scan);
    } catch (e) {
        error.value = e?.name === 'NotAllowedError'
            ? t('common.barcode.noPermission')
            : t('common.barcode.cameraFailed');
    }
};

watch(() => props.show, (open) => {
    if (open) start();
    else stop();
});

const onKeydown = (event) => {
    if (props.show && event.key === 'Escape') { event.preventDefault(); close(); }
};

onMounted(() => document.addEventListener('keydown', onKeydown));
onUnmounted(() => document.removeEventListener('keydown', onKeydown));
onBeforeUnmount(stop);
</script>

<template>
    <div v-if="show" class="fixed inset-0 z-[80] flex items-start justify-center p-4 sm:p-8 overflow-y-auto">
        <div class="fixed inset-0 bg-[#211E1A]/45" @click="close"></div>

        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="v2-scan-title"
            class="v2-sheet relative w-full max-w-[520px] bg-white rounded-2xl shadow-[0_24px_60px_-20px_rgba(33,30,26,0.5)] mt-6 mb-8 overflow-hidden"
        >
            <div class="px-6 pt-5 pb-4 flex items-start justify-between gap-4">
                <span>
                    <h2 id="v2-scan-title" class="v2-display text-[20px] leading-none text-[#211E1A]">{{ t('common.barcode.title') }}</h2>
                    <p class="text-[12px] text-[#8A8378] mt-1.5">{{ t('common.barcode.hint') }}</p>
                </span>
                <button
                    type="button"
                    :title="t('common.actions.close')"
                    class="w-7 h-7 rounded-full flex items-center justify-center text-[#8A8378] hover:text-[#211E1A] hover:bg-[#F2ECE1] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C0764B]"
                    @click="close"
                >
                    <i class="pi pi-times text-xs"></i>
                </button>
            </div>

            <div class="px-6">
                <div class="relative rounded-xl overflow-hidden bg-[#1B1813] aspect-[4/3]">
                    <video ref="videoEl" class="w-full h-full object-cover" muted playsinline></video>
                    <!-- Ramka celownika: pokazuje, gdzie trzymać kod -->
                    <span
                        v-if="!error"
                        class="absolute inset-x-8 top-1/2 -translate-y-1/2 h-20 border-2 border-dashed border-[#F5F1E9]/70 rounded-lg pointer-events-none"
                    ></span>
                    <p v-if="error" class="absolute inset-0 flex items-center justify-center px-6 text-center text-[12.5px] text-[#F5F1E9]">
                        {{ error }}
                    </p>
                </div>
            </div>

            <div class="px-6 py-4 mt-4 border-t border-[#EFE9DF]">
                <label class="v2-label" for="v2-scan-manual">{{ t('common.barcode.manual') }}</label>
                <div class="flex items-end gap-2">
                    <input
                        id="v2-scan-manual"
                        v-model="manual"
                        type="text"
                        inputmode="numeric"
                        class="v2-input v2-input--num flex-1"
                        :placeholder="t('common.barcode.placeholder')"
                        @keydown.enter.prevent="found(manual)"
                    />
                    <button
                        type="button"
                        class="h-[38px] px-4 rounded-[10px] bg-[#211E1A] text-[#FBF9F4] text-[12.5px] font-semibold hover:bg-[#302B26] disabled:opacity-60 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C0764B]"
                        :disabled="!manual.trim()"
                        @click="found(manual)"
                    >
                        {{ t('common.actions.search') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.v2-sheet {
    animation: v2-sheet-in 0.18s ease-out;
}

@keyframes v2-sheet-in {
    from { opacity: 0; transform: translateY(-6px) scale(0.99); }
    to { opacity: 1; transform: none; }
}

@media (prefers-reduced-motion: reduce) {
    .v2-sheet {
        animation: none;
    }
}
</style>
